// The three things every phase of a scan needs and nothing else owns:
// telling the analyst where it is, noticing a cancellation, and giving the
// event loop a turn.
//
// They live here, and not in `analyzer.ts`, because of import direction: the
// phase modules are called *by* `analyzeJar`, so importing a helper back out
// of it would be a cycle that drags the whole scan in behind a function that
// does not need it. `analyzer.ts`, `archive-walk.ts` and `detectors.ts` all
// import from this module; this module imports only a type.

import type { ScanProgress } from "./types";

/** Throws if the caller cancelled. Every loop in a long phase calls this. */
export function ensureNotAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw new DOMException("Scan cancelled", "AbortError");
}

export function reportProgress(
  callback: ((progress: ScanProgress) => void) | undefined,
  phase: ScanProgress["phase"],
  percent: number,
  detail: string,
) {
  callback?.({ phase, percent: Math.max(0, Math.min(100, percent)), detail });
}

const taskScheduler = (
  globalThis as { scheduler?: { yield?: () => Promise<void> } }
).scheduler;

let yieldChannel: MessageChannel | null = null;
const pendingYields: Array<() => void> = [];

function messageChannelYield() {
  if (!yieldChannel) {
    yieldChannel = new MessageChannel();
    yieldChannel.port1.onmessage = () => pendingYields.shift()?.();
    yieldChannel.port1.start();
  }
  const channel = yieldChannel;
  return new Promise<void>((resolve) => {
    // One queued resolver per posted message keeps concurrent scans in order.
    pendingYields.push(resolve);
    channel.port2.postMessage(null);
  });
}

// Only a document can be backgrounded, and Node keeps a live MessagePort
// referenced, which would stop a test process from ever exiting.
const inDocument = typeof document !== "undefined";

/**
 * Hand the event loop a turn.
 *
 * A walk over a 16,000-entry archive parses classes in a tight loop. Without
 * this the tab stops painting and a cancel click is not dispatched until the
 * archive is done, which defeats the point of being able to cancel.
 */
export async function nextFrame() {
  if (inDocument && typeof taskScheduler?.yield === "function") {
    await taskScheduler.yield();
    return;
  }
  if (inDocument && typeof MessageChannel === "function") {
    await messageChannelYield();
    return;
  }
  await new Promise<void>((resolve) => setTimeout(resolve, 0));
}
